import { apiClient } from "@/lib/api";
import type { Permission } from "@/types";

export const StaffRolePreset = {
  SUPER_ADMIN: "super_admin",
  DEVICE_TECH: "device_tech",
  DATA_ENTRY: "data_entry",
  ACCOUNTING: "accounting",
  USER_MANAGEMENT: "user_management",
} as const;
export type StaffRolePreset =
  (typeof StaffRolePreset)[keyof typeof StaffRolePreset];

export interface StaffAccessProfile {
  user_id: string;
  preset_key: StaffRolePreset | null;
  is_super_admin: boolean;
  permissions: Permission[];
  updated_by_user_id: string | null;
  updated_at: string;
}

export interface StaffRolePresetRead {
  key: StaffRolePreset;
  is_super_admin: boolean;
  permissions: Permission[];
}

export const staffAccessKeys = {
  all: ["staff-access"] as const,
  presets: ["staff-access", "presets"] as const,
  access: (userId: string) => ["staff-access", userId] as const,
};

export type StaffAccessProfileUpdate =
  | { mode: "preset"; preset_key: StaffRolePreset }
  | { mode: "custom"; permissions: Permission[] };

export const staffAccessService = {
  listPresets: async () => {
    const response = await apiClient.get<StaffRolePresetRead[]>(
      "/admin/staff/presets",
    );
    return response.data;
  },

  getAccess: async (userId: string) => {
    const response = await apiClient.get<StaffAccessProfile>(
      `/admin/staff/${userId}/access`,
    );
    return response.data;
  },

  updateAccess: async (userId: string, body: StaffAccessProfileUpdate) => {
    const response = await apiClient.put<StaffAccessProfile>(
      `/admin/staff/${userId}/access`,
      body,
    );
    return response.data;
  },

  create: async (body: {
    email: string;
    name: string;
    preset_key: StaffRolePreset;
    region_id: string;
    contact_phone: string;
  }) => {
    const response = await apiClient.post<{
      user_id: string;
      email: string;
      name: string;
      profile: StaffAccessProfile;
    }>("/admin/staff", body);
    return response.data;
  },
};
